import { makeAutoObservable } from 'mobx';
import environment from '@menu-environment';
import { HubConnection, HubConnectionBuilder, LogLevel } from '@microsoft/signalr';
import { CacheAreaKey } from 'core/config/cache-config';
import { ProfileUser } from 'core/models/account/profile';
import { LiveChatConfig, LiveChatModel, Message } from 'core/models/live-chat';
import { SubscriptionDTO } from 'core/models/product/subscription.dto';
import { ProfileService } from 'core/services/api/profile';
import { SubscriptionService } from 'core/services/api/subscriptions';
import { StorageHelper } from 'core/services/storage';
import { AuthUtils } from 'core/stores/auth/auth-utils';
import { SiteConfigStore } from 'core/stores/site-config.store';
import { LiveChatCallbackName, LiveChatOperationName } from './live-chat-types';

export enum LiveChatPages {
  LiveChat,
  EndChat,
  Error,
}

export class LiveChatStore {
  customerNumber!: string;
  country!: string;
  activePage: Partial<LiveChatPages> = LiveChatPages.LiveChat;
  showEvent: boolean = false;
  isChatOpen: boolean = false;
  isInitLoading: boolean = false;
  isChatLoading: boolean = false;
  isChatMinimised: boolean = false;
  connection!: HubConnection;
  subscription!: SubscriptionDTO;
  messages: Message[] = [];
  events: Message[] = [];
  chatModel?: LiveChatModel;
  user!: ProfileUser;

  get latestEvent() {
    return this.showEvent && this.events?.length ? this.events[0] : undefined;
  }

  get isLoading() {
    return this.isInitLoading || this.isChatLoading;
  }

  get smartcardNumber() {
    return this.subscription?.smartcardNumber as string;
  }

  constructor() {
    makeAutoObservable(this);
  }

  initialize(customerNumber: string, country: string, shouldOpen = false) {
    this.changeIsInitLoading(true);

    this.changeCountry(country);
    this.changeCustomerNumber(customerNumber);

    Promise.all([
      ProfileService.getProfile(customerNumber, country),
      SubscriptionService.getSubscriptions(customerNumber, country)
    ])
      .then(([profileUser, subscriptions]) => {
        const subscription = subscriptions.subscriptions.find(
          (sub) => sub.isPrimarySc
        );

        if (!subscription?.smartcardNumber) {
          this.changeIsInitLoading(false);
          this.changeActivePage(LiveChatPages.Error);
          console.error('Failed to chat init.');
          return;
        }

        this.changeSubscription(subscription);
        this.changeUser(profileUser);

        if (shouldOpen) {
          this.startChat();
          this.changeIsChatOpen(true);
        }
        this.changeIsInitLoading(false);
      })
      .catch((error) => {
        this.changeIsInitLoading(false);
        this.changeActivePage(LiveChatPages.Error);
        console.error('Failed to retrieve user profile.', error);
      })
  }

  startChat() {
    this.changeIsChatLoading(true);
    try {
      const connection = new HubConnectionBuilder()
        .withUrl(`${environment.DSTV_UTILITY_SERVICE}/${environment.LIVE_CHAT_HUB}`)
        .withAutomaticReconnect([0, 1000, 10000])
        .configureLogging(LogLevel.Information)
        .build();

      this.registerEvents(connection);
      this.changeConnection(connection);

      connection
        .start()
        .then(() => {
          const data = {
            Channel: SiteConfigStore.channel(),
            BusinessUnit: SiteConfigStore.businessUnit(),
            Country: this.country,
            SmartcardNumber: this.subscription?.smartcardNumber,
            CustomerNumber: this.customerNumber,
            DisplayName: `${this.user.firstName} ${this.user.surname}`,
            FirstName: this.user.firstName,
            LastName: this.user.surname,
            ChatProduct: SiteConfigStore.businessUnit(),
            AuthToken: AuthUtils.GetStoredToken()
          };

          connection
            .invoke(LiveChatOperationName.Initiate, data)
            .catch((error: any) => {
              console.error(error);
            });

        })
        .catch((error: any) => {
          this.changeIsChatLoading(false);
          this.changeActivePage(LiveChatPages.Error);
          console.error(error);
        });
    } catch (e) {
      // console.log('START ERROR -> ', e)
      this.changeIsChatLoading(false);
      this.changeActivePage(LiveChatPages.Error);
    }
  }

  sendMessage(message: string) {
    try {
      const messageModel = {
        Channel: SiteConfigStore.channel(),
        Country: this.country,
        BusinessUnit: SiteConfigStore.businessUnit(),
        Message: message,
        MessageText: message,
        SmartcardNumber: this.subscription?.smartcardNumber,
        CustomerNumber: this.customerNumber,
        Token: this.chatModel?.token,
        SenderType: 'Customer',
        ...(this.chatModel?.conversationId ? { ConversationId: this.chatModel?.conversationId } : {}),
        ...(this.chatModel?.chatHubConnectionId ? { ChatHubConnectionId: this.chatModel?.chatHubConnectionId } : {}),
        ...(this.chatModel?.websocketId ? { WebsocketId: this.chatModel?.websocketId } : {}),
      }

      this.connection.invoke(LiveChatOperationName.SendMessage, messageModel).then((res: any) => {
        if (environment.LIVE_CHAT_CONFIG.sentMessageTracking) {
          this.changeMessages([
            ...this.messages,
            {
              ...messageModel,
              isIncomingMessage: false,
            } as any
          ]);
        }
      })
    } catch (e) {
      console.error('Failed to send message. ', e);
    }
  }

  disconnectChat(closeChat = true) {
    try {
      this.connection
        .invoke(LiveChatOperationName.Disconnect, {
          Channel: SiteConfigStore.channel(),
          Country: this.country,
          BusinessUnit: SiteConfigStore.businessUnit(),
          SmartcardNumber: this.subscription?.smartcardNumber,
          CustomerNumber: this.customerNumber,
          Token: this.chatModel?.token,
          ...(this.chatModel?.conversationId ? { ConversationId: this.chatModel?.conversationId } : {}),
          ...(this.chatModel?.chatHubConnectionId ? { ChatHubConnectionId: this.chatModel?.chatHubConnectionId } : {}),
          ...(this.chatModel?.websocketId ? { WebsocketId: this.chatModel?.websocketId } : {}),
        })
        .then(_ => {
          if (this.connection) {
            this.connection.stop();
            this.changeConnection(undefined);
            this.changeChatModel(undefined);
            StorageHelper.GetInstance.removeFromStorage('chat', CacheAreaKey.LiveChat);
          }
        })

    } catch (e) {
      console.error('Failed to send message. ', e);
    }


    if (closeChat) {
      this.changeMessages([]);
      this.changeIsChatOpen(false);
      this.changeActivePage(LiveChatPages.LiveChat);
    }
  }

  receiveMessage(messageResponse: any) {
    if (this.isLoading) {
      this.changeIsInitLoading(false);
      this.changeIsChatLoading(false);
    }
    try {
      const message = JSON.parse(messageResponse) as Message;

      if (message.ConversationStatus === 'Disconnected') {
        this.disconnectChat(false);
      }

      const agentEvents = ['agent has join the chat\n', 'agent has disconnected\n'];
      // NOTE we've been told to exclude senderType null as there are irrelevant messages that come through, 
      //  though there are also relevant one's as per above.
      if (message.SenderType === null) {
        if (agentEvents.some(event => message.Message?.toLowerCase() === event)) {
          this.changeShowEvent(true);
          this.changeEvents([...this.events, message]);
        }
        return;
      }

      // NOTE: from API team - null means its system, human means agent, and customer means customer
      //  'System' and 'Agent' are retained for the legacy API 
      const isIncomingMessage = (
        message.SenderType === 'System' ||
        message.SenderType === 'Agent' ||
        message.SenderType === 'Bot' ||
        message.SenderType === 'Human'
      );

      // NOTE: we've been told not to reference MessageType
      // if (message.MessageType === 'Event') {
      //   this.changeShowEvent(true);
      //   this.changeEvents([...this.events, message]);
      //   return;
      // }

      this.changeShowEvent(false);
      this.changeMessages([...this.messages, {
        isIncomingMessage: isIncomingMessage,
        ...message
      }]);

    } catch (e: any) {
      console.error('Failed to parse message. ', e)
    }
  }

  registerEvents(hub: any) {
    hub.on(LiveChatCallbackName.Initiate, (message: string) => {
      this.changeIsChatLoading(false);
      this.changeIsInitLoading(false);

      if (message) {
        const newConfig = JSON.parse(message);
        const config: LiveChatConfig = {
          Channel: SiteConfigStore.channel(),
          Country: this.country,
          BusinessUnit: SiteConfigStore.businessUnit(),
          SmartcardNumber: this.subscription?.smartcardNumber,
          CustomerNumber: this.customerNumber,
          token: newConfig?.token,
          conversationId: newConfig?.conversationId,
          chatHubConnectionId: newConfig?.chatHubConnectionId,
          websocketId: newConfig?.websocketId
        };

        const storedConfig = (StorageHelper<LiveChatModel>).GetInstance.getFromCache('chat', `${this.customerNumber}`, CacheAreaKey.LiveChat);

        if (storedConfig) {
          this.changeChatModel(storedConfig as LiveChatModel);

          this.connection
            .invoke(LiveChatOperationName.MapConnection, storedConfig)
            .then(_ => {
              this.changeActivePage(LiveChatPages.LiveChat);
            })
            .catch((error: any) => {
              this.changeChatModel(config);
              StorageHelper.GetInstance.setCacheAndReturn('chat', CacheAreaKey.LiveChat, config, `${this.customerNumber}`);
              console.error(error);
            });

          return;
        }

        this.changeActivePage(LiveChatPages.LiveChat);

        this.changeChatModel(config);
        StorageHelper.GetInstance.setCacheAndReturn('chat', CacheAreaKey.LiveChat, this.chatModel, `${this.customerNumber}`);

        return;
      }

      this.changeActivePage(LiveChatPages.Error);
    });

    hub.on(LiveChatCallbackName.ReceiveMessage, (messageResponse: string) => {
      this.receiveMessage(messageResponse);
    });

    hub.on(LiveChatCallbackName.MapConnection, (messageResponse: string) => {
      if (messageResponse !== 'true') {
        StorageHelper.GetInstance.removeFromStorage('chat', CacheAreaKey.LiveChat);
      }
    });

    // NOTE: Can be used for debugging sent messages.
    // hub.on(LiveChatCallbackName.SentMessage, (messageResponse: string) => {
    //   console.log('Message Sent Event -> ', messageResponse)
    // });
  }

  changeCustomerNumber(customerNumber: string) {
    this.customerNumber = customerNumber;
  }

  changeCountry(country: string) {
    this.country = country;
  }

  changeConnection(connection: any) {
    this.connection = connection;
  }

  changeMessages(messages: Message[]) {
    this.messages = messages;
  }

  changeEvents(events: Message[]) {
    this.events = events;
  }

  changeChatModel(model: LiveChatModel | undefined) {
    this.chatModel = model;
  }

  changeUser(user: ProfileUser) {
    this.user = user;
  }

  changeActivePage(page: LiveChatPages) {
    this.activePage = page;
  }

  changeSubscription(subscription: SubscriptionDTO) {
    this.subscription = subscription;
  }

  changeShowEvent(showEvent: boolean) {
    this.showEvent = showEvent;
  }

  changeIsInitLoading(loading: boolean) {
    this.isInitLoading = loading;
  }

  changeIsChatLoading(loading: boolean) {
    this.isChatLoading = loading;
  }

  changeIsChatMinimised(loading: boolean) {
    this.isChatMinimised = loading;
  }

  changeIsChatOpen(open: boolean) {
    this.isChatOpen = open;
  }
}